import { type Entitlement, type Person, UNKeys, type UNLine } from 'shur-api'
import { fallbackNoDetailsAvailableDataSection } from './SHUREmptyDataCreator'
import { coverageConceptIconData } from './SHURDataboxData'
import { type DataBlockArrayCreator } from './SHURDataBlockCreator'
import type { DataBlock, IconDataSpan } from 'de-zurich-ui-library'

import { t } from '@/i18n'

const supplementaryCoverIconData: IconDataSpan = {
  iconClass: 'icon-vorteile',
  href: 'openModal_supplementaryCover',
  isRouterLink: false
}

const progressiveScaleInfoIconData: IconDataSpan = {
  iconClass: 'icon-vorteile',
  href: 'openModal_progressiveScaleInfo',
  isRouterLink: false
}

const disabilityCovExistsInfoIconData: IconDataSpan = {
  iconClass: 'icon-vorteile',
  href: 'openModal_disabilityCovExistsInfo',
  isRouterLink: false
}

export class SHURDataAccidentCreator implements DataBlockArrayCreator {
  createDatablockArray(unLine: UNLine, person: Person): DataBlock[] {
    return [
      createInsuredPersonBlock(person),
      createAccidentGlobalData(unLine, person),
      createAccidentInvalidity(person),
      createAccidentAccidentPension(person),
      createAccidentDeathTerm(person),
      ...(createAllEntitlementSections(person) ?? []),
      createAccidentTransTerm(person)
    ]
  }
}

// Versicherte Person
function createInsuredPersonBlock(person: Person): DataBlock {
  if (!person.details || person.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = person.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.ACCIDENT.INSURED_PERSON')
    },
    data: [
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.AcademicTitle}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.AcademicTitle
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.FirstName}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.FirstName
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.LastName}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.LastName
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.TitleOfNobility}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.TitleOfNobility
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.NamePrefix}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.NamePrefix
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.NameSuffix}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.NameSuffix
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.DateOfBirth}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.DateOfBirth
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.Job}`),
        value: contractDetailsCodeValuePairs.find((entry) => entry.key === UNKeys.Job)
          ?.value
      }
    ]
  }
}

// Versicherungsumfang
function createAccidentGlobalData(unLine: UNLine, person: Person): DataBlock {
  if (!person.details || person.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = person.details

  return <DataBlock>{
    title: {
      label:
        t('shurDetails.sections.ACCIDENT.SCOPE_OF_INSURANCE') ??
        'shurDetails.sections.ACCIDENT.SCOPE_OF_INSURANCE'
    },
    data: [
      {
        label:
          t('shurDetails.sections.coverageConcept.title') ??
          'shurDetails.sections.coverageConcept.title',
        value: contractDetailsCodeValuePairs.some(
          (entry) => entry.key === UNKeys.CoverageConcept
        )
          ? t(
              `shurDetails.sections.coverageConcept.${
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === UNKeys.CoverageConcept
                )!.value
              }`
            )
          : null,
        iconSpan: contractDetailsCodeValuePairs.some(
          (entry) => entry.key === UNKeys.CoverageConcept
        )
          ? coverageConceptIconData(
              String(
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === UNKeys.CoverageConcept
                )?.value
              ),
              unLine.productSubType,
              String(
                contractDetailsCodeValuePairs.find(
                  (entry) => entry.key === UNKeys.ProductVersion
                )?.value
              )
            )
          : null
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.Dynamic}`),
        value: contractDetailsCodeValuePairs.find((entry) => entry.key === UNKeys.Dynamic)
          ?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.SupplementaryCover}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.SupplementaryCover
        )?.value,
        iconSpan: supplementaryCoverIconData
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.SupplementaryExpirationDate}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.SupplementaryExpirationDate
        )?.value
      }
    ]
  }
}

// Invaliditätsleistung
function createAccidentInvalidity(person: Person): DataBlock {
  if (!person.details || person.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = person.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.ACCIDENT.INV')
    },
    data: [
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.DisabilityCovDynamic}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.DisabilityCovDynamic
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.DisabilityCovEgd}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.DisabilityCovEgd
        )?.value,
        iconSpan: supplementaryCoverIconData
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.ProgressiveScale}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.ProgressiveScale
        )?.value,
        iconSpan: progressiveScaleInfoIconData
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.DisabilityCovExists}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.DisabilityCovExists
        )?.value,
        iconSpan: disabilityCovExistsInfoIconData
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.RecurringPaymentDisability}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.RecurringPaymentDisability
        )?.value
      }
    ]
  }
}

// Unfall-Rente
function createAccidentAccidentPension(person: Person): DataBlock {
  if (!person.details || person.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = person.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.ACCIDENT.ACCIDENT_PENSION')
    },
    data: [
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.LimitAccidentPension}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.LimitAccidentPension
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.LimitAccidentPensionEgd}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.LimitAccidentPensionEgd
        )?.value,
        iconSpan: supplementaryCoverIconData
      }
    ]
  }
}

// Todesfallleistung
function createAccidentDeathTerm(person: Person): DataBlock {
  if (!person.details || person.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = person.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.ACCIDENT.DEATH_TERM')
    },
    data: [
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.LimitDeathTerm}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.LimitDeathTerm
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.LimitDeathTermEgd}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.LimitDeathTermEgd
        )?.value,
        iconSpan: supplementaryCoverIconData
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.AssignmentCreditors}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.AssignmentCreditors
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.AssignmentCreditorsFixedSum}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.AssignmentCreditorsFixedSum
        )?.value
      },
      {
        label: t(
          `shurDetails.sections.ACCIDENT.${UNKeys.AssignmentCreditorsPercentageShare}`
        ),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.AssignmentCreditorsPercentageShare
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.AssignmentCreditorsDeath}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.AssignmentCreditorsDeath
        )?.value
      }
    ]
  }
}

function createAllEntitlementSections(person: Person): DataBlock[] | null {
  if (!person.entitlements || person.entitlements.length === 0) return null

  const entitlementSections: DataBlock[] = [] as DataBlock[]

  person.entitlements.forEach((entitlement, idx) =>
    entitlementSections.push(createAccidentSingleEntitlement(entitlement, idx + 1))
  )

  return entitlementSections
}

// Bezugsberechtigte Person (kann mehrfach vorkommen)
function createAccidentSingleEntitlement(
  entitlement: Entitlement,
  idx: number
): DataBlock {
  if (!entitlement) return {} as DataBlock

  return <DataBlock>{
    title: {
      label: idx + '. ' + t('shurDetails.sections.ACCIDENT.BENEFICIARY')
    },
    data: [
      {
        label: t(`shurDetails.sections.ACCIDENT.TYPE_OF_ENTITLEMENT`),
        value: entitlement.typeOfEntitlement,
        hide: !entitlement.typeOfEntitlement
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.FirstName}`),
        value: entitlement.firstName,
        hide: !entitlement.firstName
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.LastName}`),
        value: entitlement.lastName,
        hide: !entitlement.lastName
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.DateOfBirth}`),
        value: entitlement.dateOfBirth,
        hide: !entitlement.dateOfBirth
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.ENTITLEMENTS_FIXED_SUM`),
        value: entitlement.fixedSum,
        hide: !entitlement.fixedSum
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.ENTITLEMENTS_PERCENTAGE_SHARE`),
        value: entitlement.percentageShare,
        hide: !entitlement.percentageShare
      }
    ]
  }
}

// Leistungserweiterung
function createAccidentTransTerm(person: Person): DataBlock {
  if (!person.details || person.details.length === 0)
    return fallbackNoDetailsAvailableDataSection()

  const contractDetailsCodeValuePairs = person.details

  return <DataBlock>{
    title: {
      label: t('shurDetails.sections.ACCIDENT.TRANS_TERM')
    },
    data: [
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.LimitImprovTransAidTerm}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.LimitImprovTransAidTerm
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.LimitHospitalAllowance}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.LimitHospitalAllowance
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.LimitHospitalAllowanceEgd}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.LimitHospitalAllowanceEgd
        )?.value,
        iconSpan: supplementaryCoverIconData
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.DailyAllowance}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.DailyAllowance
        )?.value
      },
      {
        label: t(`shurDetails.sections.ACCIDENT.${UNKeys.InstantAidBoneFracture}`),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.InstantAidBoneFracture
        )?.value
      },
      {
        label: t(
          `shurDetails.sections.ACCIDENT.${UNKeys.NewAssistancePackRehabManagement}`
        ),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.NewAssistancePackRehabManagement
        )?.value
      },
      {
        label: t(
          `shurDetails.sections.ACCIDENT.${UNKeys.AccidentImprovedDismembermentPhysicians}`
        ),
        value: contractDetailsCodeValuePairs.find(
          (entry) => entry.key === UNKeys.AccidentImprovedDismembermentPhysicians
        )?.value
      }
    ]
  }
}
